import { escapeHTML as esc } from '../core/utils.js';
import { videoToMusicTime, noteName, chordLabel } from '../core/music.js';
import { tempoClock, staffMeasures, staffLayout, noteStaff, staffSegments, staffRests, staffPages, staffPageAt, staffX, beamGroups } from '../core/notation.js';
import { catalogEntry, catalogMeasures } from '../core/project-catalog.js';
import { musicSymbol, timeSignature } from './music-symbols.js';
import { keyboardRange, defaultKeyboardKeys } from '../core/display-layout.js';
import { WidgetEditor, widgetEditorStyles } from './widget-editor.js';

const DEFAULT_VIEWS = { falling: true, keyboard: true, chords: true, sheet: false };
const BLACK = new Set([1, 3, 6, 8, 10]);
const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const NATURAL = [0, 2, 4, 5, 7, 9, 11];
const COLORS = ['var(--green)', 'var(--accent)', '#8b9ec5', '#c38c9f'];
const isBlack = pitch => BLACK.has(pitch % 12);
const number = value => Math.round(value * 100) / 100;

// The practice staff keeps performance timing while displaying rhythmic notation.
function staffPosition(note, layout) {
  const pitch = note.pitch;
  const letter = LETTERS.indexOf(note.step);
  const spelled = letter >= 0 && (note.octave + 1) * 12 + NATURAL[letter] + (note.alter || 0) === pitch;
  const octave = spelled ? note.octave : Math.floor(pitch / 12) - 1;
  const index = spelled ? letter : NATURAL.findLastIndex(semitone => semitone <= pitch % 12);
  const step = octave * 7 + index;
  const staff = noteStaff(note, layout), treble = staff === 1;
  const bottom = 88 + (layout ? Math.max(0, layout.staves.indexOf(staff)) : treble ? 0 : 1) * 84;
  const baseline = treble ? 4 * 7 + 2 : 2 * 7 + 4; // E4 and G2.
  return { y: bottom - (step - baseline) * 5, top: bottom - 40, bottom, treble,
    accidental: spelled ? (note.alter || 0) : (isBlack(pitch) ? 1 : 0) };
}

function ledgerLines(y, top, bottom, x) {
  let lines = '';
  for (let at = bottom + 10; at <= y; at += 10) lines += `<path d="M${number(x - 10)} ${at}h20"/>`;
  for (let at = top - 10; at >= y; at -= 10) lines += `<path d="M${number(x - 10)} ${at}h20"/>`;
  return lines;
}

function noteGlyph(x, y, position, rhythm, beam) {
  const down = beam?.down ?? y <= position.bottom - 20, direction = down ? 1 : -1;
  const stemX = x + (down ? -6 : 6), stemEnd = beam?.endY ?? y + direction * 29;
  let glyph = `<ellipse class="notehead ${rhythm.open ? 'open' : ''}" cx="${number(x)}" cy="${y}" rx="${rhythm.stem ? 6.5 : 8}" ry="4.5" transform="rotate(-20 ${number(x)} ${y})"/>`;
  if (rhythm.stem) glyph += `<path class="stem" d="M${number(stemX)} ${y}V${stemEnd}"/>`;
  for (let flag = 0; !beam && flag < rhythm.flags; flag++) {
    const yy = stemEnd - direction * flag * 6;
    glyph += `<path class="flag" d="M${number(stemX)} ${yy}c12 ${-direction * 5} 13 ${-direction * 11} 6 ${-direction * 18}c3 ${direction * 9} -2 ${direction * 10} -6 ${direction * 11}Z"/>`;
  }
  const dotY = (y - position.bottom) % 10 === 0 ? y - 5 : y;
  for (let dot = 0; dot < rhythm.dots; dot++) glyph += `<circle class="dot" cx="${number(x + 12 + dot * 5)}" cy="${dotY}" r="1.8"/>`;
  if (rhythm.tuplet) glyph += `<text x="${number(x)}" y="${down ? stemEnd + 12 : stemEnd - 5}" font-size="10" stroke="none" text-anchor="middle">${rhythm.tuplet}</text>`;
  return glyph;
}

function restGlyph(x, y, rhythm) {
  let glyph;
  if (rhythm.type === 'whole') glyph = '<path d="M-7-10H7v5H-7Z"/>';
  else if (rhythm.type === 'half') glyph = '<path d="M-7-5H7v5H-7Z"/>';
  else if (rhythm.type === 'quarter') glyph = '<path d="M-3-16 5-6 0 1 6 10C-5 3-7 8-2 16-12 9-10 2-4 0L-9-6-2-13Z"/>';
  else {
    glyph = `<path d="M5-10 ${-2 - (rhythm.flags - 1) * 1.5} ${12 + (rhythm.flags - 1) * 6}" fill="none" stroke="currentColor" stroke-width="1.8"/>`;
    for (let flag = 0; flag < rhythm.flags; flag++) {
      const yy = -5 + flag * 6, xx = -flag * 1.5;
      glyph += `<path d="M${xx+5} ${yy-3}C${xx+3} ${yy+2} ${xx-1} ${yy+4} ${xx-5} ${yy+2}C${xx-8} ${yy} ${xx-6} ${yy-5} ${xx-3} ${yy-4}C${xx} ${yy-3} ${xx-1} ${yy} ${xx-1} ${yy}Q${xx+2} ${yy+1} ${xx+5} ${yy-3}Z"/>`;
    }
  }
  for (let dot = 0; dot < rhythm.dots; dot++) glyph += `<circle class="dot" cx="${12 + dot * 5}" cy="-5" r="1.8"/>`;
  if (rhythm.tuplet) glyph += `<text x="0" y="-20" font-size="10" text-anchor="middle">${rhythm.tuplet}</text>`;
  return `<g class="rest-symbol" transform="translate(${number(x)} ${y})">${glyph}</g>`;
}

function drawBeams(groups, visible, x, layout) {
  const stems = new Map();
  let markup = '';
  for (const group of groups) {
    const members = group.filter(segment => visible.has(segment));
    const events = [];
    for (const segment of members) {
      if (events.at(-1)?.[0].start === segment.start) events.at(-1).push(segment);
      else events.push([segment]);
    }
    if (events.length < 2) continue;
    const positions = members.map(s => staffPosition(s.note, layout)), middle = positions[0].bottom - 20;
    const down = middle - Math.min(...positions.map(p => p.y)) >= Math.max(...positions.map(p => p.y)) - middle;
    const direction = down ? 1 : -1, stemX = s => x(s.start) + (down ? -6 : 6);
    const firstX = stemX(members[0]), lastX = stemX(members.at(-1));
    const rise = Math.max(-7, Math.min(7, positions.at(-1).y - positions[0].y));
    const slope = rise / (lastX - firstX);
    const ends = positions.map((p, i) => p.y + direction * 30 - slope * (stemX(members[i]) - firstX));
    const startY = down ? Math.max(...ends) : Math.min(...ends), beamY = xx => startY + slope * (xx - firstX);
    for (const segment of members) stems.set(segment, { down, endY: beamY(stemX(segment)) });
    const beam = (from, to, level) => {
      const y1 = beamY(from) - direction * level * 6, y2 = beamY(to) - direction * level * 6, thickness = -direction * 4;
      markup += `<path class="beam" d="M${number(from)} ${number(y1)}L${number(to)} ${number(y2)}v${thickness}L${number(from)} ${number(y1+thickness)}Z"/>`;
    };
    const flags = Math.max(...members.map(s => s.rhythm.flags));
    for (let level = 0; level < flags; level++) {
      for (let i = 0; i < events.length; i++) {
        if (events[i][0].rhythm.flags <= level) continue;
        const first = i;
        while (i + 1 < events.length && events[i + 1][0].rhythm.flags > level) i++;
        const from = stemX(events[first][0]);
        if (i > first) beam(from, stemX(events[i][0]), level);
        else {
          const neighbor = stemX(events[first ? first - 1 : first + 1][0]);
          beam(from, from + Math.sign(neighbor-from) * Math.min(10, Math.abs(neighbor-from)*.4), level);
        }
      }
    }
  }
  return { stems, markup };
}

/** Local music views. Call update(score, videoSeconds) after edits, and
 * updateTime(videoSeconds) from the player's own clock. No independent timer. */
export class MusicViews extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.views = { ...DEFAULT_VIEWS };
    this.notes = [];
    this.mediaTime = 0;
    this.windowSeconds = 6;
    this.keyPointers = new Map();
  }

  connectedCallback() {
    if (this.shadowRoot.childElementCount) {
      this.widgetEditor?.connect();
      // Practice panes can be detached from the shadow tree while the view moves.
      if (this.resizeObserver) for (const node of [this, this.panes?.falling.querySelector('.roll'), this.panes?.sheet.querySelector('.staff')].filter(Boolean)) this.resizeObserver.observe(node);
      return;
    }
    this.shadowRoot.innerHTML = `<style>
      :host{display:block;margin-top:18px;color:var(--text,#28312e);font:inherit;--music-warning:var(--danger,#ab4d40)}
      *{box-sizing:border-box}[hidden]{display:none!important}button,select{font:inherit;color:inherit}button{cursor:pointer;touch-action:manipulation}button:focus,select:focus,[tabindex]:focus{outline:none}button:focus-visible:not([data-pointer-focus]),select:focus-visible:not([data-pointer-focus]),[tabindex]:focus-visible:not([data-pointer-focus]){outline:2px solid var(--accent-strong);outline-offset:3px}
      .card{border:1px solid var(--border,#e3e4db);border-radius:15px;background:var(--surface,#fffefa);overflow:hidden}.heading{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 18px 10px}.heading h3{font-size:14px;margin:0;font-weight:600}.clock{font-size:11px;font-variant-numeric:tabular-nums;color:var(--muted);white-space:nowrap}.title{padding:0 18px;font-size:12px;color:var(--muted);margin:0;overflow-wrap:anywhere}
      .toggles{display:flex;flex-wrap:wrap;gap:6px;padding:14px 18px}.toggles button{padding:8px 11px;min-height:40px;border:1px solid var(--border);border-radius:7px;background:transparent;font-size:11px}.toggles button[aria-pressed=true]{background:var(--accent-soft);border-color:var(--accent);color:var(--accent-strong)}.toggles label{margin-left:auto;display:flex;gap:7px;align-items:center;font-size:11px;color:var(--muted)}select{background:var(--surface);border:1px solid var(--border);border-radius:6px;padding:8px;min-height:40px}
      .empty{font-size:12px;color:var(--muted);padding:0 18px 18px;margin:0}.music-scroll{overflow:hidden;min-width:0}.roll,.keyboard{display:block;width:100%;max-width:none}.roll{height:210px;background:var(--surface-2)}.keyboard{height:90px;background:var(--surface-2)}.roll .guide{stroke:var(--border);stroke-width:1}.roll .now{stroke:var(--green);stroke-width:2}.roll .note{stroke:var(--surface);stroke-width:1}:host([review-markers]) .roll .pending{stroke:var(--music-warning);stroke-width:2;stroke-dasharray:4 3;opacity:.7}.roll text{font-size:11px;fill:var(--text);font-weight:600;pointer-events:none}.key-white{fill:#fcfbf7;stroke:#8c968e;stroke-width:1}.key-black{fill:#25342d;stroke:#15231c;stroke-width:1}.key.active{fill:var(--accent,#dfb77a)}.key.active.restrike{animation:key-restrike 130ms ease-out}@keyframes key-restrike{0%,35%{fill:#76552c}100%{fill:var(--accent,#dfb77a)}}.key-label{font-size:11px;fill:#43534a;pointer-events:none}.key-label.black{fill:#f8f6ed;font-size:9px}.key-label.active{fill:#352a1d}
      .chords{display:flex;align-items:center;gap:13px;padding:14px 18px;border-top:1px solid var(--border)}.chords strong{font-size:20px;font-weight:500;min-width:100px}.chords span{font-size:11px;color:var(--muted)}.chords .notes{display:block;margin-top:3px;color:var(--text)}.sheet-wrap{padding:13px 16px 8px;border-top:1px solid var(--border)}.sheet-head{display:flex;justify-content:space-between;align-items:center;gap:10px}.sheet-head h4{font-size:12px;margin:0;font-weight:600}.sheet-head span{font-size:10px;color:var(--muted)}.staff-scroll{overflow:hidden;min-width:0}.staff{display:block;min-width:0;width:100%;height:var(--staff-height,226px)}.staff .staff-line{stroke:var(--muted);opacity:.6;stroke-width:1}.staff .clef,.staff .time-signature,.staff .beam{fill:var(--text);stroke:none}.staff .note{fill:var(--text);stroke:var(--text);cursor:pointer}.staff .note.active{fill:var(--accent-strong);stroke:var(--accent-strong)}:host([review-markers]) .staff .note.pending{fill:var(--music-warning);stroke:var(--music-warning)}.staff .notehead.open{fill:var(--surface)}:host([practice]) .staff .notehead.open{fill:var(--surface-2)}.staff .stem{stroke-width:1.3;fill:none}.staff .tie{fill:none;stroke-width:1.2}.staff .barline{stroke:var(--muted);stroke-width:1}.staff .playhead{stroke:var(--accent);stroke-width:2;pointer-events:none}.staff .accidental{font-size:20px;stroke:none}.staff .measure{font-size:10px;fill:var(--muted)}.staff .note:focus{outline:none}.staff .note{outline:none;-webkit-tap-highlight-color:transparent}.staff .note:focus-visible:not([data-pointer-focus]){filter:drop-shadow(0 0 3px var(--accent-strong))}.staff .note:focus-visible:not([data-pointer-focus]) .notehead{stroke:var(--accent-strong);stroke-width:2.5}@media(hover:hover) and (pointer:fine){.staff .note:hover:not([data-touch-hover]){fill:var(--accent-strong);stroke:var(--accent-strong);filter:drop-shadow(0 0 3px var(--accent-strong))}.staff .note:hover:not([data-touch-hover]) .notehead{stroke-width:2.5}}
      .legend{margin:0;padding:9px 18px 13px;font-size:10px;line-height:1.6;color:var(--muted)}.legend .pending{display:none;color:var(--music-warning)}:host([review-markers]) .legend .pending{display:inline}.active-notes{padding:0 18px 13px;display:flex;flex-wrap:wrap;gap:5px}.active-notes:empty{display:none}.active-notes button{border:1px solid var(--border);border-radius:5px;padding:5px 8px;min-height:36px;background:var(--surface-2);font-size:10px}:host([review-markers]) .active-notes button.pending{border-style:dashed;border-color:var(--music-warning)}
      @media(max-width:600px){.heading,.toggles{padding-left:12px;padding-right:12px}.toggles label{margin-left:0}.sheet-head{align-items:start;flex-direction:column;gap:4px}.chords{padding:12px}.chords strong{min-width:85px;font-size:18px}.roll{height:170px}.title{padding:0 12px}}
      .music-scroll,.keyboard-scroll{overflow:hidden;min-width:0}
      .staff .rest{fill:var(--text);color:var(--text);stroke:none;pointer-events:none}
      .roll,.keyboard{margin-inline:auto;flex:none}.roll text{font-size:13px;font-weight:500}
      .key-label{font-size:12px;fill:#718078}.key-label.black{font-size:12px;fill:#aab6ae}.key-label.active{fill:#665638}
      :host([practice]){display:block;margin:0;min-width:0;background:var(--surface)}
      :host([practice]) .card{border:0;border-radius:0;overflow:visible}
      :host([practice]) :is(.heading,.title,.toggles,.legend,.active-notes){display:none}
      :host([practice]) .visuals{display:block}
      .practice-columns{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px;padding:10px 12px 8px;align-items:start}
      .practice-columns.single-column{grid-template-columns:minmax(0,1fr)}
      .practice-column{display:flex;flex-direction:column;gap:8px;min-width:0}
      .practice-pane{min-width:0;overflow:hidden;border-radius:6px}
      :host([practice]) .roll{height:clamp(180px,26dvh,310px);background:var(--surface-2)}
      :host([practice]) .keyboard{height:auto;background:var(--surface-2)}
      :host([practice]) .chords{padding:10px 12px;border:0;background:var(--surface-2);border-radius:6px;min-height:48px}
      :host([practice]) .chords strong{font-size:19px;min-width:60px}
      :host([practice]) .sheet-wrap{border:0;padding:4px 6px;background:var(--surface-2)}
      :host([practice]) .sheet-head{display:none}
      :host([practice]) .staff{height:calc(var(--staff-height,226px) * .79646);min-width:0}
      ::slotted(mt-video-player){width:100%;min-width:0}
      .pane-video.youtube-mini{max-width:320px;width:100%;min-width:200px;margin-inline:auto}
      .practice-columns:has(.youtube-mini) .practice-column{min-width:200px}
      :host([practice]) .visuals{-webkit-user-select:none;user-select:none}
      /* Reserve the whole gesture, including diagonal starts, for scrubbing.
         Put touch-action on HTML wrappers too: Safari can pan an SVG's parent. */
      :host([practice]) :is(.music-scroll,.staff-scroll,.roll,.staff){touch-action:none;overscroll-behavior:none;-webkit-touch-callout:none}
      /* SVG notes are replaced on each redraw. Start gestures on persistent
         HTML so iPadOS keeps the original touch target across those redraws. */
      :host([practice]) :is(.roll,.staff){pointer-events:none}
      :host([practice]) .music-scroll{cursor:ns-resize}
      :host([practice]) .staff-scroll{cursor:ew-resize}
      .keyboard{touch-action:none;-webkit-user-select:none;user-select:none;-webkit-touch-callout:none}
      .keyboard,.keyboard *{-webkit-tap-highlight-color:transparent}
      .keyboard [data-pitch],.keyboard [data-pitch]:focus-visible:not([data-pointer-focus]){cursor:pointer;outline:none}
      .keyboard [data-pitch]:focus-visible:not([data-pointer-focus]) .key:not(.active){filter:brightness(.85)}
      :host([expanded]) .card,:host([expanded]) .visuals{height:100%}
      :host([expanded]) .practice-columns{height:100%;align-items:stretch}
      :host([expanded]) .practice-column{min-height:0}
      :host([expanded]) .pane-falling{flex:1 1 180px;min-height:160px;display:flex}
      :host([expanded]) .pane-falling .music-scroll{width:100%;height:100%}
      :host([expanded]) .roll{height:100%;min-height:160px}
      :host([expanded]) .pane-video:not(.youtube-mini){flex:1 1 200px;min-height:200px;display:flex;align-items:center}
      :host([expanded]) .pane-keyboard,:host([expanded]) .pane-chords,:host([expanded]) .pane-sheet{flex:none}
      :host([expanded]) .staff{height:clamp(calc(var(--staff-height,226px) * .708),calc(18dvh * var(--staff-ratio,1)),calc(var(--staff-height,226px) * 1.195))}
      @media(max-width:600px){:host([expanded]) .practice-columns{height:auto;min-height:100%}}
      @media(max-width:600px){.practice-columns{grid-template-columns:minmax(0,1fr);padding:8px;gap:8px}}
      :host([practice]) .pane-falling{display:flex;flex-direction:column}
      :host([practice]) .pane-falling .music-scroll{width:100%;height:100%;flex:1;min-height:0}
      :host([practice]) .pane-falling .roll{height:100%;min-height:0}
      :host([practice]) .practice-pane.custom-height{flex:0 0 auto}
      :host([practice]) .pane-keyboard.custom-height .keyboard-scroll{height:100%}
      :host([practice]) .pane-chords.custom-height .chords{height:100%}
      ${widgetEditorStyles}
    </style><section class="card" aria-label="Music views">
      <div class="heading"><h3>See what you’re playing</h3><output class="clock" aria-label="Music time">0:00.0</output></div>
      <p class="title"></p>
      <div class="toggles" role="group" aria-label="Music views">${Object.entries({ falling: 'Falling notes', keyboard: 'Keyboard names', chords: 'Chords', sheet: 'Practice staff' }).map(([key, title]) => `<button type="button" data-view="${key}" aria-pressed="true">${title}</button>`).join('')}<label>Look ahead <select aria-label="Seconds of music shown"><option value="4">4 seconds</option><option value="6" selected>6 seconds</option><option value="10">10 seconds</option></select></label></div>
      <p class="empty">Import MIDI, MusicXML, or extracted notes to start practising.</p>
      <div class="visuals" hidden><div class="music-scroll" tabindex="0" role="region" aria-label="Falling notes and named keyboard"><svg class="roll" viewBox="0 0 1000 210" preserveAspectRatio="none" role="img" aria-label="Upcoming notes descending toward the keyboard"></svg><svg class="keyboard" viewBox="0 0 1000 90" preserveAspectRatio="none" role="group" aria-label="Piano keyboard with active notes highlighted"></svg></div>
      <div class="chords"><strong>—</strong><span>Chord suggestion · from sounding notes<span class="notes">No notes sounding</span></span></div>
      <div class="sheet-wrap"><div class="sheet-head"><h4>Practice staff</h4><span>Measured notation · spaced to fit</span></div><div class="staff-scroll" tabindex="0" role="region" aria-label="Piano staff; automatically advances by measure"><svg class="staff" viewBox="0 0 760 226" role="group" aria-label="Practice notation"></svg></div></div>
      <p class="legend">All views follow the practice clock. <span class="pending">Dashed falling notes and highlighted staff notes need review.</span> Select a note to review its pitch and timing. <span class="density"></span></p><div class="active-notes" aria-label="Sounding notes to review"></div></div>
    </section>`;
    this.shadowRoot.addEventListener('click', event => {
      if (this.hasAttribute('layout-editing') || event.target.closest('.widget-overlay')) return;
      const button = event.target.closest('[data-view]');
      if (button) {
        const key = button.dataset.view;
        this.views[key] = !this.views[key];
        this.applyViews();
        this.dispatchEvent(new CustomEvent('music-views-change', { detail: { views: { ...this.views } }, bubbles: true, composed: true }));
      }
      if (this.suppressNoteClick > performance.now()) return;
      const key = event.target.closest('.keyboard [data-pitch]');
      if (key && event.detail === 0) this.previewKey(Number(key.dataset.pitch));
      const note = event.target.closest('[data-note]');
      if (note) this.selectNote(note.dataset.note);
    });
    this.shadowRoot.addEventListener('pointerdown', event => { if (!this.startKey(event)) this.startSeek(event); });
    this.shadowRoot.addEventListener('pointermove', event => { if (!this.moveKey(event)) this.moveSeek(event); });
    this.shadowRoot.addEventListener('pointerup', event => { this.finishKey(event); this.finishSeek(event); });
    this.shadowRoot.addEventListener('pointercancel', event => { this.finishKey(event); this.finishSeek(event); });
    this.shadowRoot.addEventListener('lostpointercapture', event => { this.finishKey(event); this.finishSeek(event); });
    for (const surface of this.shadowRoot.querySelectorAll('.music-scroll,.staff-scroll')) {
      // Safari's custom touch gestures need cancellable touchstart/touchmove
      // events on a target that remains connected for the entire gesture.
      surface.addEventListener('touchstart', event => this.startSeek(event), { passive: false });
      surface.addEventListener('touchmove', event => this.moveSeek(event), { passive: false });
      surface.addEventListener('touchend', event => this.finishSeek(event), { passive: false });
      surface.addEventListener('touchcancel', event => this.finishSeek(event), { passive: false });
    }
    this.shadowRoot.addEventListener('keydown', event => {
      if(this.hasAttribute('layout-editing') && event.target.closest('.keyboard,.staff')) { event.preventDefault();return; }
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('.keyboard [data-pitch]')) {
        event.preventDefault(); event.stopPropagation(); this.previewKey(Number(event.target.dataset.pitch)); return;
      }
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('g[data-note]')) {
        event.preventDefault();
        this.selectNote(event.target.dataset.note);
      }
    });
    this.shadowRoot.querySelector('select').addEventListener('change', event => {
      this.windowSeconds = Number(event.target.value);
      this.updateTime(this.mediaTime, true);
    });
    this.applyViews();
    if (this.score) this.update(this.score, this.mediaTime);
  }
  disconnectedCallback() { this.clearPreview(true); this.resizeObserver?.disconnect();cancelAnimationFrame(this.geometryFrame);this.widgetEditor?.disconnect(); }

  mountPractice(video) {
    this.setAttribute('practice','');
    if (this.practiceMounted) return;
    this.practiceMounted=true;
    const root=this.shadowRoot,visuals=root.querySelector('.visuals'),columns=document.createElement('div');
    columns.className='practice-columns';columns.innerHTML='<div class="practice-column" data-column="0"></div><div class="practice-column" data-column="1"></div>';
    this.panes={};
    const keyboardScroll=document.createElement('div');keyboardScroll.className='keyboard-scroll';keyboardScroll.tabIndex=0;keyboardScroll.setAttribute('role','region');keyboardScroll.setAttribute('aria-label','Piano keyboard');keyboardScroll.append(root.querySelector('.keyboard'));
    const slot=document.createElement('slot');slot.name='video';video.slot='video';this.append(video);
    for(const [key,node] of Object.entries({video:slot,falling:root.querySelector('.music-scroll'),keyboard:keyboardScroll,sheet:root.querySelector('.sheet-wrap'),chords:root.querySelector('.chords')})) {
      const pane=document.createElement('section');pane.className='practice-pane pane-'+key;pane.dataset.pane=key;pane.append(node);this.panes[key]=pane;
    }
    visuals.prepend(columns);this.columns=columns;
    this.widgetEditor=new WidgetEditor(this);
    const rollScroll=this.panes.falling.querySelector('.music-scroll');
    for (const scroll of [rollScroll,keyboardScroll]) scroll.addEventListener('scroll',()=>{
      if(this.panes.falling.parentElement!==this.panes.keyboard.parentElement||this.rollWidth!==this.pianoWidth)return;
      const other=scroll===keyboardScroll?rollScroll:keyboardScroll;
      if(Math.abs(other.scrollLeft-scroll.scrollLeft)>1)other.scrollLeft=scroll.scrollLeft;
    });
    this.resizeObserver=new ResizeObserver(()=>{cancelAnimationFrame(this.geometryFrame);this.geometryFrame=requestAnimationFrame(()=>this.updateGeometry());});this.resizeObserver.observe(this);this.resizeObserver.observe(this.panes.falling.querySelector('.roll'));this.resizeObserver.observe(this.panes.sheet.querySelector('.staff'));
  }
  configurePractice(layout,{videoVisible,musicAvailable,mini,editing=false,orientation='landscape',projectId,itemId}) {
    this.toggleAttribute('review-markers', layout.reviewMarkers === true);
    this.toggleAttribute('layout-editing',editing);
    this.practiceLayout=layout;this.keyLabels=layout.keyLabels;this.windowSeconds=layout.lookAhead;this.staffPlayhead=layout.staffPlayhead===true;
    this.updateKeyRange();
    this.views={falling:musicAvailable&&layout.enabled.falling,keyboard:musicAvailable&&layout.enabled.keyboard,sheet:musicAvailable&&layout.enabled.sheet,chords:musicAvailable&&layout.enabled.chords};
    const visible={...this.views,video:videoVisible};
    for(let column=0;column<2;column++) {
      const parent=this.columns.children[column];
      layout.columns[column].forEach((key,index)=>{
        const pane=this.panes[key];pane.hidden=!visible[key];
        if(parent.children[index]!==pane)parent.insertBefore(pane,parent.children[index]||null);
      });
    }
    // Moving a pane into the second column also removes it from the first.
    // Measure visibility only after both columns have their final contents.
    for (const parent of this.columns.children) parent.hidden=![...parent.children].some(pane=>!pane.hidden);
    this.columns.classList.toggle('single-column',[...this.columns.children].filter(column=>!column.hidden).length<2);
    this.panes.video.classList.toggle('youtube-mini',mini);
    this.querySelector('mt-video-player')?.classList.toggle('youtube-mini',mini);
    this.shadowRoot.querySelector('.visuals').hidden=false;this.shadowRoot.querySelector('.empty').hidden=true;
    this.applyWidgetSizes(layout);this.applyViews();this.updateGeometry();
    this.widgetEditor.update(layout,{editing,orientation,projectId,itemId});
  }
  applyWidgetSizes(layout) {
    if(!this.panes)return;
    this.sizingLayout=layout;
    const fallingHeight=Math.round(Math.max(200,Math.min(900,innerHeight*.32*layout.lookAhead/6)));
    for(const [key,pane] of Object.entries(this.panes)) {
      const size=layout.widgetSizes?.[key],height=size?.height;
      pane.style.width=(size?.width||100)+'%';
      pane.style.height=height?height+'px':key==='falling'?fallingHeight+'px':'';
      pane.style.minHeight=key==='falling'?(height||fallingHeight)+'px':'';
      pane.classList.toggle('custom-height',!!height);
      if(key==='sheet')pane.querySelector('.staff').style.height=height?Math.max(84,height-12)+'px':'';
      if(key==='video') {
        const video=this.querySelector('mt-video-player');
        video?.classList.toggle('widget-sized',!!height);
        video?.style.setProperty('--widget-video-height',height?height+'px':'auto');
      }
    }
    this.geometrySignature=null;this.updateGeometry();this.widgetEditor?.refreshSizes();
  }
  updateGeometry() {
    if(!this.keys?.length)return;
    const root=this.shadowRoot,white=this.keys.filter(key=>!key.black).length;
    const widthFor=element=>Math.max(1,Math.min(element?.clientWidth||this.clientWidth||600,white*60));
    const keyboardScroll=this.panes?.keyboard.hidden?null:root.querySelector('.keyboard-scroll');
    const pianoWidth=widthFor(keyboardScroll||root.querySelector('.music-scroll')),rollWidth=pianoWidth;
    const pianoHeight=this.panes?.keyboard.classList.contains('custom-height')?this.panes.keyboard.clientHeight:Math.max(80,Math.min(144,pianoWidth/white*2.7));
    const rollHeight=root.querySelector('.roll').clientHeight||210;
    const staff=root.querySelector('.staff'),staffHeight=(this.staffLayout?.height||226)-(this.staffInset||0);
    const staffWidth=Math.max(240,(staff.clientWidth||760)*staffHeight/(staff.clientHeight||180));
    const signature=[rollWidth,pianoWidth,pianoHeight,rollHeight,staffWidth,staffHeight,this.keyLabels].join(':');
    if(signature===this.geometrySignature)return;this.geometrySignature=signature;
    Object.assign(this,{rollWidth,pianoWidth,pianoHeight,rollHeight,staffWidth});
    staff.setAttribute('viewBox', `0 ${this.staffInset||0} ${staffWidth} ${staffHeight}`);
    const roll=root.querySelector('.roll'),keyboard=root.querySelector('.keyboard');
    roll.style.width=rollWidth+'px';roll.style.minWidth=rollWidth+'px';roll.setAttribute('viewBox',`0 0 ${rollWidth} ${rollHeight}`);
    keyboard.style.width=pianoWidth+'px';keyboard.style.minWidth=pianoWidth+'px';keyboard.style.height=pianoHeight+'px';keyboard.setAttribute('viewBox',`0 0 ${pianoWidth} ${pianoHeight}`);
    this.keyboardSignature=null;this.staffPages=null;this.updateTime(this.mediaTime,true);
    this.widgetEditor?.refreshSizes();
  }


  startSeek(event) {
    if (!this.hasAttribute('practice') || this.hasAttribute('layout-editing') || !this.score || event.pointerType === 'touch' || event.target.closest('.keyboard')) return;
    const touch=event.changedTouches?.[0],point=touch||event;
    if (!touch && (event.button !== 0 || event.isPrimary === false)) return;
    const capture=event.target.closest('.music-scroll,.staff-scroll');
    const surface=capture?.querySelector('.roll,.staff');
    if (!surface) return;
    event.preventDefault();
    if (this.seekDrag || (touch && event.touches.length !== 1)) return;
    const vertical=surface.classList.contains('roll'),rect=surface.getBoundingClientRect();
    const width=this.staffWidth||760,page=this.staffPage;
    const size=vertical?rect.height:rect.width*(page? page.right-page.left : width)/width;
    const seconds=vertical?this.windowSeconds:page? page.end-page.start : this.windowSeconds;
    // Keep the drag scale fixed while seeking changes the displayed staff page.
    this.seekDrag={capture,vertical,note:this.seekNoteAt(surface,point),input:touch?'touch':'pointer',id:touch?touch.identifier:event.pointerId,x:point.clientX,y:point.clientY,time:this.musicTime(this.mediaTime),secondsPerPixel:seconds/Math.max(1,size),moved:false};
    if (!touch) capture.setPointerCapture(event.pointerId);
  }
  seekNoteAt(surface, point) {
    // Keep note taps and keyboard activation while the artwork itself is not
    // a pointer target. A small hit margin also helps with tiny staff notes.
    let closest, distance=Infinity;
    for (const glyph of surface.querySelectorAll('[data-note] .notehead,rect.note')) {
      const rect=glyph.getBoundingClientRect(),x=point.clientX,y=point.clientY;
      if (x<rect.left-4 || x>rect.right+4 || y<rect.top-4 || y>rect.bottom+4) continue;
      const d=Math.hypot(x-(rect.left+rect.right)/2,y-(rect.top+rect.bottom)/2);
      if (d<distance) { closest=glyph.closest('[data-note]').dataset.note;distance=d; }
    }
    return closest;
  }
  seekPoint(event) {
    const drag=this.seekDrag;
    if (!drag) return;
    if (drag.input === 'touch') return [...(event.changedTouches||[])].find(touch=>touch.identifier===drag.id);
    if (!event.changedTouches && event.pointerId===drag.id) return event;
  }
  moveSeek(event) {
    const point=this.seekPoint(event),drag=this.seekDrag;if(!point)return;
    event.preventDefault();
    const delta=drag.vertical?point.clientY-drag.y:drag.x-point.clientX;
    if(!drag.moved&&Math.abs(delta)<5)return;
    drag.moved=true;
    drag.value=Math.max(0,drag.time+delta*drag.secondsPerPixel);
    this.dispatchEvent(new CustomEvent('music-seek',{detail:{musicTime:drag.value,preview:true},bubbles:true,composed:true}));
  }
  finishSeek(event) {
    const drag=this.seekDrag;if(!this.seekPoint(event))return;
    const released=event.type==='pointerup'||event.type==='touchend';
    // Include the release position even if the browser coalesced the last move.
    if(released)this.moveSeek(event);
    this.seekDrag=null;
    if(drag.input==='pointer'&&drag.capture.hasPointerCapture(drag.id))drag.capture.releasePointerCapture(drag.id);
    if(!drag.moved){
      if(drag.note && released){this.suppressNoteClick=performance.now()+500;this.selectNote(drag.note);}
      return;
    }
    this.suppressNoteClick=performance.now()+500;
    this.dispatchEvent(new CustomEvent('music-seek',{detail:{musicTime:drag.value},bubbles:true,composed:true}));
  }
  startKey(event) {
    if(this.hasAttribute('layout-editing'))return false;
    const key = event.target.closest('.keyboard [data-pitch]');
    if (!key || event.button !== 0) return false;
    event.preventDefault();
    const keyboard = this.shadowRoot.querySelector('.keyboard'), pitch = Number(key.dataset.pitch);
    this.keyPointers.set(event.pointerId, pitch);
    keyboard.setPointerCapture(event.pointerId);
    this.previewKey(pitch, true); return true;
  }
  moveKey(event) {
    if (!this.keyPointers.has(event.pointerId)) return false;
    event.preventDefault();
    const key = this.shadowRoot.elementFromPoint(event.clientX, event.clientY)?.closest('.keyboard [data-pitch]');
    const pitch = key ? Number(key.dataset.pitch) : null, previous = this.keyPointers.get(event.pointerId);
    if (pitch === previous) return true;
    this.keyPointers.set(event.pointerId, pitch); this.releaseKey(previous);
    if (pitch !== null) this.previewKey(pitch, true);
    return true;
  }
  finishKey(event) {
    if (!this.keyPointers.has(event.pointerId)) return;
    const pitch = this.keyPointers.get(event.pointerId); this.keyPointers.delete(event.pointerId);
    const keyboard = this.shadowRoot.querySelector('.keyboard');
    if (keyboard.hasPointerCapture(event.pointerId)) keyboard.releasePointerCapture(event.pointerId);
    this.releaseKey(pitch);
  }
  releaseKey(pitch) {
    if (pitch === null || [...this.keyPointers.values()].includes(pitch)) return;
    clearTimeout(this.previewPitches?.get(pitch)); this.previewPitches?.delete(pitch);
    this.updateTime(this.mediaTime, true);
  }
  previewKey(pitch, held = false) {
    this.dispatchEvent(new CustomEvent('music-key-preview',{detail:{pitch},bubbles:true,composed:true}));
    this.previewPitches ??= new Map();
    clearTimeout(this.previewPitches.get(pitch));
    this.previewPitches.set(pitch,held || [...this.keyPointers.values()].includes(pitch) ? null : setTimeout(()=>{this.previewPitches.delete(pitch);this.updateTime(this.mediaTime,true);},850));
    this.updateTime(this.mediaTime,true);

  }
  clearPreview(releaseKeys = false) {
    if (releaseKeys) {
      const keyboard = this.shadowRoot.querySelector('.keyboard');
      for (const id of this.keyPointers.keys()) if (keyboard?.hasPointerCapture(id)) keyboard.releasePointerCapture(id);
      this.keyPointers.clear();
    }
    this.previewPitches?.forEach(timer=>clearTimeout(timer));this.previewPitches?.clear();
    for (const pitch of this.keyPointers.values()) if (pitch !== null) this.previewPitches?.set(pitch, null);
    this.updateTime(this.mediaTime,true);
  }

  selectNote(id) {
    this.dispatchEvent(new CustomEvent('music-note-select', { detail: { id }, bubbles: true, composed: true }));
    const note = this.score?.notes.find(note => note.id === id);
    if (note && this.hasAttribute('practice')) this.dispatchEvent(new CustomEvent('music-key-preview', { detail: { pitch: note.pitch }, bubbles: true, composed: true }));
  }

  update(score, mediaTime = this.mediaTime) {
    if(this.score?.id!==score?.id){this.keyboardStrikes=null;this.clearPreview(true);this.keyboardStrikes=null;}
    this.score = score;
    this.mediaTime = mediaTime;
    this.views = { ...DEFAULT_VIEWS, ...score?.views };
    this.notes = (score?.notes || []).filter(note => !note.rejected).slice().sort((a, b) => a.start - b.start);
    const library = catalogEntry(score?.source?.name?.match(/^library-(.+)\.mid$/)?.[1]);
    this.measures = score ? staffMeasures({ ...score, notes: this.notes, measures: score.measures?.length ? score.measures : library ? catalogMeasures(library) : [] }) : [];
    const clock = tempoClock(score?.tempos);
    this.staffLayout = staffLayout(this.notes);
    this.staffInset = 0;
    this.staffNotes = staffSegments(this.notes, this.measures, clock);
    this.staffRests = staffRests(this.notes, this.measures, clock, this.staffLayout);
    this.beamGroups = beamGroups(this.staffNotes, this.measures, clock, this.staffLayout);
    this.staffPages = null;
    this.maxEnds = [];
    let end = 0;
    for (const note of this.notes) { end = Math.max(end, note.start + note.duration); this.maxEnds.push(end); }
    this.updateKeyRange();
    if (!this.shadowRoot.childElementCount) return;
    const staff = this.shadowRoot.querySelector('.staff');
    staff.style.setProperty('--staff-height', `${this.staffLayout.height}px`);
    staff.style.setProperty('--staff-ratio', this.staffLayout.height / 226);
    staff.dataset.staves = this.staffLayout.staves.join(',');
    staff.setAttribute('aria-label', `${this.staffLayout.staves.length > 1 ? 'Treble and bass staves' : this.staffLayout.staves[0] === 2 ? 'Bass staff' : 'Treble staff'} synchronized to practice`);
    const populated = this.notes.length > 0;
    this.shadowRoot.querySelector('.visuals').hidden = !populated;
    this.shadowRoot.querySelector('.empty').hidden = populated;
    this.shadowRoot.querySelector('.title').textContent = score ? `${score.title || score.source?.name || 'Imported music'} · ${this.notes.length.toLocaleString()} notes` : '';
    this.applyViews();
    this.updateGeometry();
    this.updateTime(mediaTime, true);
  }

  updateKeyRange() {
    // The selected key count is shared by the keyboard and falling notes.
    let low = 60, high = 83;
    if (this.notes.length) {
      const range = this.notes.reduce((range, note) => [Math.min(range[0], note.pitch), Math.max(range[1], note.pitch)], [127, 0]);
      low = Math.max(0, Math.floor(range[0] / 12) * 12);
      high = Math.min(127, Math.ceil((range[1] + 1) / 12) * 12 - 1);
      if (high - low < 23) { low = Math.max(0, low - 12); high = Math.min(127, low + 23); }
    }
    if(this.hasAttribute('practice'))({low,high}=keyboardRange(this.notes,this.practiceLayout?.keyboardKeys||defaultKeyboardKeys(this.notes)));
    const signature=low+':'+high;if(this.keyRangeSignature===signature)return;this.keyRangeSignature=signature;
    this.keys = [];
    let white = 0;
    for (let pitch = low; pitch <= high; pitch++) {
      const black = isBlack(pitch);
      this.keys.push({ pitch, black, unit: black ? white - .32 : white });
      if (!black) white++;
    }
    this.whiteCount=white;this.geometrySignature=null;
    this.keyWidth = 1000 / white;
    this.keyByPitch = new Map(this.keys.map(key => [key.pitch, key]));
  }

  applyViews() {
    if (!this.shadowRoot.childElementCount) return;
    for (const [key, enabled] of Object.entries(this.views)) {
      this.shadowRoot.querySelector(`[data-view="${key}"]`)?.setAttribute('aria-pressed', String(enabled));
    }
    for (const [key, selector] of Object.entries({ falling: '.roll', keyboard: '.keyboard', chords: '.chords', sheet: '.sheet-wrap' })) this.shadowRoot.querySelector(selector).hidden = !this.views[key];
    this.shadowRoot.querySelector('.music-scroll').hidden = this.practiceMounted ? !this.views.falling : !this.views.falling && !this.views.keyboard;
    this.shadowRoot.querySelector('select').parentElement.hidden = !this.views.falling && !this.views.sheet;
    this.updateTime(this.mediaTime, true);
  }

  overlapping(from, to) {
    let low = 0, high = this.notes.length;
    while (low < high) { const mid = (low + high) >>> 1; if (this.notes[mid].start <= to) low = mid + 1; else high = mid; }
    const result = [];
    for (let i = low - 1; i >= 0 && this.maxEnds[i] > from; i--) {
      const note = this.notes[i];
      if (note.start + note.duration > from) result.push(note);
    }
    return result.reverse();
  }

  get musicEnd() { return this.score?.duration ?? this.maxEnds?.at(-1) ?? 0; }
  musicTime(mediaTime) {
    return Math.min(videoToMusicTime(this.score, mediaTime), this.musicEnd);
  }
  updateTime(mediaTime, force = false) {
    this.mediaTime = Number.isFinite(mediaTime) ? mediaTime : 0;
    if (!this.score || !this.notes.length || !this.shadowRoot.childElementCount) return;
    const time = this.musicTime(this.mediaTime);
    if (!force && (time === this.lastPaint || time !== this.musicEnd && Math.abs(time - this.lastPaint) < 1 / 30)) return;
    this.lastPaint = time;
    this.shadowRoot.querySelector('.clock').textContent = `${Math.floor(Math.max(0, time) / 60)}:${(Math.max(0, time) % 60).toFixed(1).padStart(4, '0')}`;
    const visible = this.overlapping(time, time + this.windowSeconds);
    const active = [...visible.filter(note => note.start <= time && note.start + note.duration > time), ...[...(this.previewPitches?.keys()||[])].map(pitch=>({pitch,id:'preview-'+pitch,reviewed:true}))];
    if (this.views.falling) this.drawRoll(visible, time);
    if (this.views.keyboard) this.drawKeyboard(active, time);
    if (this.views.chords) {
      this.shadowRoot.querySelector('.chords strong').textContent = chordLabel(active) || '—';
      this.shadowRoot.querySelector('.chords .notes').textContent = active.length ? [...new Set(active.map(note => noteName(note.pitch)))].join(' · ') : 'No notes sounding';
    }
    if (this.views.sheet) this.drawStaff(time, force);
    this.shadowRoot.querySelector('.density').textContent = visible.length > 600 ? 'Dense passage: staff displays up to 600 notes per page; falling notes display up to 1,500.' : '';
    const activeSignature = active.slice(0, 16).map(note => `${note.id}:${note.reviewed}`).join('|');
    if (force || this.activeSignature !== activeSignature) {
      this.activeSignature = activeSignature;
      this.shadowRoot.querySelector('.active-notes').innerHTML = active.slice(0, 16).map(note => `<button type="button" data-note="${esc(note.id)}" class="${this.pending(note) ? 'pending' : ''}" title="Review ${esc(noteName(note.pitch))}">${esc(noteName(note.pitch))}${Number.isFinite(note.confidence) ? ` · ${Math.round(note.confidence * 100)}%` : ''}</button>`).join('');
    }
  }

  pending(note) { return !note.reviewed && (this.score?.source?.reviewed === false || Number.isFinite(note.confidence)); }
  noteLabel(note) { return `${noteName(note.pitch)}, ${note.start.toFixed(2)} seconds, duration ${note.duration.toFixed(2)} seconds${Number.isFinite(note.confidence) ? `, confidence ${Math.round(note.confidence * 100)} percent` : ''}${this.pending(note) ? ', needs review' : ''}`; }

  drawRoll(notes, time) {
    const height=this.rollHeight||210,width=this.rollWidth||1000,gutter=0,cell=(width-gutter)/(this.whiteCount||14),scale=height/this.windowSeconds;
    const guides=this.keys.filter(key=>!key.black).map(key=>`<path class="guide" d="M${number(gutter+key.unit*cell)} 0v${height}"/>`).join('');
    const times=Array.from({length:Math.ceil(this.windowSeconds)},(_,index)=>{
      const seconds=Math.ceil(time)+index,y=number(height-(seconds-time)*scale);
      return y<0?'':`<path class="guide" d="M${gutter} ${y}H${width}"/>`;
    }).join('');
    this.shadowRoot.querySelector('.roll').innerHTML=guides+times+notes.slice(0,1500).map(note=>{
      const key=this.keyByPitch.get(note.pitch);if(!key)return '';
      const keyWidth=cell*(key.black?.64:1),w=Math.min(keyWidth,Math.max(2,keyWidth-4)),x=gutter+key.unit*cell+(keyWidth-w)/2;
      const top=Math.max(0,height-(note.start+note.duration-time)*scale),bottom=Math.min(height,height-(note.start-time)*scale);
      const label=w>=14&&bottom-top>18?`<text x="${number(x+w/2)}" y="${number(bottom-6)}" text-anchor="middle" style="font-size:${number(Math.min(13,(w-3)/(noteName(note.pitch).length*.62)))}px">${esc(noteName(note.pitch))}</text>`:'';
      return `<g data-note="${esc(note.id)}"><title>${esc(this.noteLabel(note))}</title><rect class="note ${this.pending(note)?'pending':''}" x="${number(x)}" y="${number(top)}" width="${number(w)}" height="${number(Math.max(2,bottom-top))}" rx="3" fill="${COLORS[(note.track||0)%COLORS.length]}" opacity=".95"/>${label}</g>`;
    }).join('')+`<path class="now" d="M${gutter} ${height-1}H${width}"/>`;
  }

  drawKeyboard(active, time) {
    const focusedPitch=this.shadowRoot.activeElement?.closest('.keyboard [data-pitch]')?.dataset.pitch;
    if (time < this.keyboardPaintTime || time - this.keyboardPaintTime >= .25) this.keyboardStrikes = null;
    const pitches=new Set(active.map(note=>note.pitch)),signature=this.keyLabels||'c';
    const width=this.pianoWidth||1000,height=this.pianoHeight||110,cell=width/(this.whiteCount||14),labels=this.keyLabels||'c';
    const keyboard = this.shadowRoot.querySelector('.keyboard');
    if(this.keyboardSignature!==signature||this.keyboardKeys!==this.keys) {
      this.keyboardSignature=signature;this.keyboardKeys=this.keys;
      keyboard.innerHTML=[false,true].map(black=>this.keys.filter(key=>key.black===black).map(key=>{
      const w=cell*(black?.64:1),x=key.unit*cell,on=pitches.has(key.pitch),h=black?height*.62:height-1;
      const label=labels==='all'||labels==='c'&&key.pitch%12===0;
      return `<g data-pitch="${key.pitch}" role="button" tabindex="0" aria-label="Play ${esc(noteName(key.pitch))}"><title>${esc(noteName(key.pitch))}${on?', sounding':''}</title><rect class="key key-${black?'black':'white'} ${on?'active':''}" x="${number(x)}" y="0" width="${number(w)}" height="${number(h)}" rx="2"/>${label?`<text class="key-label ${black?'black':''} ${on?'active':''}" x="${number(x+w/2)}" y="${number(h-12)}" text-anchor="middle" style="font-size:${number(Math.min(12,(w-2)/(noteName(key.pitch).length*.62)))}px">${esc(noteName(key.pitch))}</text>`:''}</g>`;
    }).join('')).join('');
      if(focusedPitch)keyboard.querySelector('[data-pitch="'+focusedPitch+'"]')?.focus({preventScroll:true});
    }
    keyboard.querySelectorAll('[data-pitch]').forEach(key => {
      const pitch = Number(key.dataset.pitch), on = pitches.has(pitch);
      const rect = key.querySelector('.key');
      rect.classList.toggle('active', on);
      const strikes = active.filter(note => note.pitch === pitch).map(note => note.id).join('|');
      const previous = this.keyboardStrikes?.get(pitch);
      if (on && previous && previous !== strikes && time >= this.keyboardPaintTime && time - this.keyboardPaintTime < .25) {
        rect.classList.remove('restrike');
        // Restart the brief dip for each new note, even at fast tempos.
        void rect.getBoundingClientRect();
        rect.classList.add('restrike');
      } else if (!on) rect.classList.remove('restrike');
      key.querySelector('.key-label')?.classList.toggle('active', on);
      key.setAttribute('aria-pressed', String(on));
      key.querySelector('title').textContent = noteName(pitch) + (on ? ', sounding' : '');
    });
    this.keyboardStrikes = new Map([...(this.keyboardStrikes || []), ...[...pitches].map(pitch => [pitch, active.filter(note => note.pitch === pitch).map(note => note.id).join('|')])]);
    this.keyboardPaintTime = time;
  }

  drawStaff(time, force) {
    const width = this.staffWidth || 760, layout = this.staffLayout;
    const tops = layout.staves.map((staff, i) => 48 + i * 84), bottom = tops.at(-1) + 40;
    const playhead = value => `M${number(value)} 23V${layout.height - 32}`;
    const layoutKey = `${width}:${this.windowSeconds}:${layout.staves.join(',')}`;
    if (!this.staffPages || this.staffLayoutKey !== layoutKey) {
      this.staffLayoutKey = layoutKey;
      this.staffPages = staffPages(this.measures, [...this.staffNotes, ...this.staffRests], width, this.windowSeconds, Math.max(32, 24 * width / (this.shadowRoot.querySelector('.staff').clientWidth || width)));
    }
    const page = staffPageAt(this.staffPages, time);
    if (!page) return;
    this.staffPage = page;
    const x = value => staffX(page, value), signature = `${layoutKey}:${page.start}:${page.end}:${this.staffPlayhead===true}`;
    const redraw = force || this.staffSignature !== signature;
    if (redraw) {
      this.staffSignature = signature;
      let markup = tops.flatMap(top => [0, 10, 20, 30, 40].map(offset => `<path class="staff-line" d="M${number(page.staffLeft)} ${top + offset}H${number(page.staffRight)}"/>`)).join('');
      if (page.opening) {
        markup += '<g class="opening-signs">' + layout.staves.map((staff, i) => musicSymbol(staff === 1 ? 'treble' : 'bass', page.staffLeft, tops[i] + (staff === 1 ? 30 : 10), 'clef')).join('');
        const meter = page.bars[0];
        if (meter.numerator && meter.denominator) for (const top of tops) markup += timeSignature(meter.numerator, page.staffLeft + 43, top + 10) + timeSignature(meter.denominator, page.staffLeft + 43, top + 30);
        markup += '</g>';
      }
      for (const bar of page.bars) {
        // A system starts with notes/signs, never a spurious leading barline.
        if (bar.start > page.start) markup += `<path class="barline" d="M${number(bar.x)} 48V${bottom}"/>`;
        markup += `<text class="measure" x="${number(Math.max(page.left,bar.x))}" y="40" text-anchor="middle">${esc(bar.number)}</text>`;
      }
      const last = page.bars.at(-1);
      if (last.complete) {
        const endX = Math.min(page.right, last.x + last.width);
        markup += `<path class="barline" d="M${number(endX)} 48V${bottom}"/>`;
        if (page.final) markup += `<path class="barline final-bar" style="stroke:var(--text);stroke-width:4" d="M${number(endX+4)} 48V${bottom}"/>`;
      }
      for (const rest of this.staffRests.filter(rest => rest.start < page.end - 1e-7 && rest.end > page.start + 1e-7)) {
        const { rhythm } = rest, bar = page.bars.find(bar => bar.index === rest.measure);
        const restX = rest.fullMeasure ? (Math.max(page.left, bar.x) + Math.min(page.right, bar.x + bar.width)) / 2 : x(Math.max(page.start, rest.start));
        const label = rest.fullMeasure ? 'Whole-measure rest' : `${rhythm.dots === 2 ? 'Double-dotted ' : rhythm.dots ? 'Dotted ' : ''}${rhythm.type} rest`;
        markup += `<g class="rest" data-staff="${rest.staff}" data-start="${rest.start}" data-end="${rest.end}" data-rhythm="${rhythm.type}" role="img" aria-label="${label}"><title>${label}</title>${restGlyph(restX, tops[layout.staves.indexOf(rest.staff)] + 20 + rest.offset, rhythm)}</g>`;
      }
      const notes = this.staffNotes.filter(n => n.start < page.end - 1e-7 && n.end > page.start + 1e-7).slice(0, 600);
      const beams = drawBeams(this.beamGroups, new Set(notes.filter(n => n.start >= page.start)), x, layout);
      for (const segment of notes) {
        const { note, rhythm } = segment, position = staffPosition(note, layout), y = position.y, startX = x(Math.max(page.start, segment.start));
        const yy = Math.max(18, Math.min(layout.height - 24, y));
        const sharp = position.accidental === 1 ? '♯' : position.accidental === -1 ? '♭' : position.accidental === 2 ? '𝄪' : position.accidental === -2 ? '𝄫' : note.accidental === 'natural' ? '♮' : '';
        const pending = this.pending(note), label = `${this.noteLabel(note)}, ${rhythm.dots ? 'dotted ' : ''}${rhythm.type} note`;
        let ties = '';
        if (segment.tieIn || segment.start < page.start) ties += `<path class="tie" d="M${number(startX-15)} ${yy+9}q7 6 14 0"/>`;
        if (segment.tieOut || segment.end > page.end) {
          const bar = page.bars.find(bar => bar.index === segment.measure), endX = Math.min(page.right - 3, segment.end < bar.start + bar.duration - .000001 ? x(segment.end) - 7 : bar.x + bar.width - 5);
          ties += `<path class="tie" d="M${number(startX+7)} ${yy+9}Q${number((startX+endX)/2)} ${yy+22} ${number(endX)} ${yy+9}"/>`;
        }
        markup += `<g class="note ${pending ? 'pending' : ''}" data-note="${esc(note.id)}" data-staff="${noteStaff(note, layout)}" data-pitch="${note.pitch}" data-start="${segment.start}" data-end="${segment.end}" data-rhythm="${rhythm.type}" tabindex="0" role="button" aria-label="${this.hasAttribute('practice') ? 'Seek to' : 'Review'} ${esc(label)}"><title>${esc(label)}</title><rect class="hit" x="${number(startX - 12)}" y="${yy - 14}" width="24" height="28" fill="transparent" stroke="none"/>${ledgerLines(yy, position.top, position.bottom, startX)}${sharp ? `<text class="accidental" x="${number(startX - 19)}" y="${yy + 6}">${sharp}</text>` : ''}${noteGlyph(startX, yy, position, rhythm, beams.stems.get(segment))}${ties}${yy === y ? '' : `<text x="${number(startX - 8)}" y="${yy < 40 ? yy - 7 : yy + 16}" font-size="10" stroke="none">${esc(noteName(note.pitch))}</text>`}</g>`;
      }
      markup += beams.markup + (this.staffPlayhead===true ? `<path class="playhead" d="${playhead(75)}"/>` : '');
      this.shadowRoot.querySelector('.staff').innerHTML = markup;
    }
    this.shadowRoot.querySelector('.staff .playhead')?.setAttribute('d', playhead(x(time)));
    this.shadowRoot.querySelectorAll('.staff .note').forEach(note => note.classList.toggle('active', (Number(note.dataset.start) <= time && Number(note.dataset.end) > time)||!!this.previewPitches?.has(Number(note.dataset.pitch))));
    const staff=this.shadowRoot.querySelector('.staff');staff.querySelector('.preview-notes')?.remove();
    if(this.previewPitches?.size){
      const markup=[...this.previewPitches.keys()].map(pitch=>{const pos=staffPosition({pitch},layout),y=Math.max(18,Math.min(layout.height-24,pos.y)),xx=x(time);return '<g class="note active">'+ledgerLines(y,pos.top,pos.bottom,xx)+'<ellipse cx="'+xx+'" cy="'+y+'" rx="7" ry="5"/>'+(pos.accidental?'<text class="accidental" x="'+(xx-20)+'" y="'+(y+6)+'">♯</text>':'')+'</g>';}).join('');
      staff.insertAdjacentHTML('beforeend','<g class="preview-notes">'+markup+'</g>');
    }
    const previews = [...(this.previewPitches?.keys() || [])].join(',');
    if (redraw || this.measurePreviews !== previews) {
      this.measurePreviews = previews;
      this.compactStaff();
    }
  }

  compactStaff() {
    const staff = this.shadowRoot.querySelector('.staff'), matrix = staff.getScreenCTM();
    if (!matrix || !staff.clientHeight) return;
    // Measure visible ink, excluding the larger transparent note hit targets.
    // Screen bounds include rotated heads and translated rests/clefs accurately.
    const ink = [...staff.querySelectorAll('.note > :not(title):not(.hit),.beam,.rest-symbol,.clef,.time-signature')].map(node => node.getBoundingClientRect());
    const gap = 3 * matrix.d;
    for (const label of staff.querySelectorAll('.measure')) {
      const box = label.getBoundingClientRect();
      const overlaps = ink.some(mark => box.left < mark.right + gap && box.right > mark.left - gap && box.top < mark.bottom + gap && box.bottom > mark.top - gap);
      label.style.visibility = overlaps ? 'hidden' : '';
    }
    // Reclaim the old number row while retaining room for actual high notes.
    const highest = Math.min(...ink.map(box => (box.top - matrix.f) / matrix.d));
    const inset = Math.max(0, Math.min(20, Math.floor(highest - 6)));
    if (inset === this.staffInset) return;
    this.staffInset = inset;
    const height = this.staffLayout.height - inset;
    staff.style.setProperty('--staff-height', `${height}px`);
    staff.style.setProperty('--staff-ratio', height / 226);
    staff.setAttribute('viewBox', `0 ${inset} ${this.staffWidth || 760} ${height}`);
  }
}

customElements.define('mt-music-views', MusicViews);
